import RxntService from '../services/RxntService';
import { OK } from './constant';

function ensureArray(d) {
  if (Array.isArray(d)) return d;
  if (d && Array.isArray(d.data)) return d.data;
  return [];
}

function rxntProviderNameFromMapping(mapping) {
  const pr = mapping?.provider || {};
  const label = `${pr.first_name || ''} ${pr.last_name || ''}`.trim();
  return label || mapping?.provider_id || '—';
}

function snapProviderNameFromLocal(local) {
  const pr = local?.provider || {};
  const label = `${pr.first_name || ''} ${pr.last_name || ''}`.trim();
  return label || local?.provider_id || '—';
}

async function runPool(items, concurrency, worker) {
  const queue = Array.isArray(items) ? items.slice() : [];
  const limit = Math.max(1, Number(concurrency) || 1);
  const runners = Array.from({ length: Math.min(limit, queue.length || 1) }, async () => {
    while (queue.length) {
      const item = queue.shift();
      // eslint-disable-next-line no-await-in-loop
      await worker(item);
    }
  });
  await Promise.all(runners);
}

/**
 * Loads RXNT gateway pending worklists for all FullyAuthorized mapped providers,
 * dedupes by RXNT prescription id (same doctor may appear on multiple mappings).
 * Same logic as the Notifications modal RXNT tab.
 *
 * Each row includes provider_id + rxnt_doctor_id for per-card RxntLaunchButton (row prescriber).
 *
 * @returns {Promise<{ rows: Array, launchProvider: { provider_id: string, rxnt_doctor_id: number } | null }>}
 */
export async function fetchRxntPendingRowsAggregated() {
  const provRes = await RxntService.getProviders();
  const mapped = ensureArray(provRes?.data);
  const usable = mapped.filter(
    (m) =>
      m?.provider_id &&
      m?.rxnt_doctor_id != null &&
      String(m.rxnt_doctor_id) !== '' &&
      m?.rxnt_provider_data?.authority === 'FullyAuthorized'
  );

  const launchProvider = usable[0]
    ? {
        provider_id: String(usable[0].provider_id),
        rxnt_doctor_id: Number(usable[0].rxnt_doctor_id),
      }
    : null;

  const out = [];
  const seen = new Set();
  await runPool(usable, 4, async (m) => {
    const res = await RxntService.listPrescriptions({
      tab: 'pending',
      provider_id: m.provider_id,
      rxnt_doctor_id: Number(m.rxnt_doctor_id),
      per_page: 300,
    });
    if (res?.code === OK) {
      const rows = Array.isArray(res.data) ? res.data : [];
      rows.forEach((item) => {
        const rx = item?.rx || item;
        const local = item?.local || null;
        const rxId = rx?.prescriptionId || local?.rxnt_prescription_id || null;
        if (rxId != null) {
          const key = String(rxId);
          if (seen.has(key)) return;
          seen.add(key);
        }
        out.push({
          key: `${m.provider_id}-${rxId || rx?.id || local?.id || Math.random()}`,
          provider_id: m.provider_id,
          // Gateway doctorId for this row's prescriber (not the modal-wide launchProvider fallback).
          rxnt_doctor_id: Number(m.rxnt_doctor_id),
          provider_name: local ? snapProviderNameFromLocal(local) : rxntProviderNameFromMapping(m),
          rx,
          local,
        });
      });
    }
  });

  out.sort((a, b) => {
    const da = new Date(a?.rx?.entryDate || a?.rx?.approvedDate || 0).getTime() || 0;
    const db = new Date(b?.rx?.entryDate || b?.rx?.approvedDate || 0).getTime() || 0;
    return db - da;
  });

  return { rows: out, launchProvider };
}
